import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Pin, PinOff, Search, Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function FeaturedArticleTab() {
  const token = sessionStorage.getItem('admin_token');
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const url = `${API_URL}/api/admin/articles/list${q ? `?q=${encodeURIComponent(q)}` : ''}`;
      const r = await fetch(url, { headers });
      const data = await r.json();
      setArticles(data.articles || []);
    } catch (e) {
      console.error(e);
      setMsg('Failed to load articles');
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => {
    e.preventDefault();
    load(search);
  };

  const pin = async (slug, order, title) => {
    setBusy(`${slug}:${order}`);
    setMsg('');
    try {
      const r = await fetch(`${API_URL}/api/admin/articles/${encodeURIComponent(slug)}/pin?order=${order}`, { method: 'POST', headers });
      if (!r.ok) throw new Error(await r.text());
      const slotLabel = order === 0 ? 'Hero' : `Slot ${order + 1}`;
      setMsg(`"${title}" pinned to ${slotLabel}.`);
      await load(search);
    } catch (e) {
      setMsg(`Failed to pin: ${e.message}`);
    }
    setBusy('');
  };

  const unpinOne = async (slug, title) => {
    setBusy(`unpin:${slug}`);
    setMsg('');
    try {
      const r = await fetch(`${API_URL}/api/admin/articles/${encodeURIComponent(slug)}/unpin`, { method: 'POST', headers });
      if (!r.ok) throw new Error(await r.text());
      setMsg(`Unpinned "${title}".`);
      await load(search);
    } catch (e) {
      setMsg(`Failed: ${e.message}`);
    }
    setBusy('');
  };

  const unpinAll = async () => {
    setBusy('unpinall');
    setMsg('');
    try {
      const r = await fetch(`${API_URL}/api/admin/articles/unpin-all`, { method: 'POST', headers });
      if (!r.ok) throw new Error(await r.text());
      setMsg('Homepage reverted to sort-by-recency (all slots cleared).');
      await load(search);
    } catch (e) {
      setMsg(`Failed: ${e.message}`);
    }
    setBusy('');
  };

  const pinnedBySlot = [0, 1, 2].map(order => articles.find(a => a.featured_order === order) || null);
  const anyPinned = pinnedBySlot.some(Boolean);

  const SLOT_META = [
    { label: 'Hero', tag: 'Main featured article', cls: 'bg-[#E63946]' },
    { label: 'Slot 2', tag: 'Sub-feature 1', cls: 'bg-amber-500' },
    { label: 'Slot 3', tag: 'Sub-feature 2', cls: 'bg-teal-500' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-featured-article-tab">
      {/* Slots overview */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Pin className="w-5 h-5 text-teal-500" /> Homepage Featured Slots
            </h2>
            <p className="text-sm text-gray-500 mt-1">Pin up to 3 articles. The Hero shows as the main banner; Slot 2 and 3 appear as sub-features on the right. Empty slots fall back to most recent articles.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={`${API_URL}/api/admin/articles/export-knowledge-base?token=${encodeURIComponent(token || '')}`}
              download
              className="px-4 py-2 text-xs font-medium text-white bg-violet-600 hover:bg-violet-700 rounded flex items-center gap-1.5"
              data-testid="featured-export-claude-md-btn"
              title="Download one consolidated .md file optimised for Claude Projects"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Export for Claude (.md)
            </a>
            <a
              href={`${API_URL}/api/admin/articles/export?token=${encodeURIComponent(token || '')}`}
              download
              className="px-4 py-2 text-xs font-medium text-white bg-gray-900 hover:bg-black rounded flex items-center gap-1.5"
              data-testid="featured-export-zip-btn"
              title="Download every published article as a ZIP (HTML + Markdown + JSON + CSV manifest)"
            >
              <FileText className="w-3.5 h-3.5" />
              Export All Articles (.zip)
            </a>
            {anyPinned && (
              <button
                onClick={unpinAll}
                disabled={busy === 'unpinall'}
                className="px-4 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="featured-unpin-all-btn"
              >
                <PinOff className="w-3.5 h-3.5" />
                {busy === 'unpinall' ? 'Clearing…' : 'Clear All'}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {SLOT_META.map((slot, i) => {
            const art = pinnedBySlot[i];
            return (
              <div
                key={slot.label}
                className={`relative rounded-lg border ${art ? 'border-teal-300 bg-teal-50/50' : 'border-dashed border-gray-300 bg-gray-50'} p-3 min-h-[110px]`}
                data-testid={`featured-slot-${i}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-block w-2 h-2 rounded-full ${slot.cls}`} />
                  <span className="text-[10px] font-bold uppercase tracking-widest text-gray-700">{slot.label}</span>
                  <span className="text-[10px] text-gray-400">· {slot.tag}</span>
                </div>
                {art ? (
                  <div className="flex items-start gap-2">
                    {art.featured_image && (
                      <img src={art.featured_image} alt="" className="w-12 h-12 rounded object-cover border border-gray-200 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-900 line-clamp-2">{art.title}</p>
                      <button
                        onClick={() => unpinOne(art.slug, art.title)}
                        disabled={busy === `unpin:${art.slug}`}
                        className="mt-1.5 text-[10px] font-medium text-red-600 hover:text-red-700 disabled:opacity-50 inline-flex items-center gap-0.5"
                        data-testid={`featured-slot-${i}-unpin`}
                      >
                        <PinOff className="w-3 h-3" /> Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">Empty — auto-fills with latest by date.</p>
                )}
              </div>
            );
          })}
        </div>

        {msg && (
          <div className="mt-3 px-3 py-2 text-xs bg-gray-100 border border-gray-200 rounded text-gray-700" data-testid="featured-status-msg">{msg}</div>
        )}
      </div>

      {/* Search + list with pin-to-slot controls */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <form onSubmit={handleSearch} className="flex gap-2 mb-4">
          <input
            type="text"
            placeholder="Search articles by title or slug…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-teal-400"
            data-testid="featured-search-input"
          />
          <button type="submit" className="px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 flex items-center gap-1.5" data-testid="featured-search-btn">
            <Search className="w-3.5 h-3.5" /> Search
          </button>
        </form>

        {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
        {loading ? (
          <p className="text-sm text-gray-400 text-center py-8">Loading…</p>
        ) : articles.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No articles match.</p>
        ) : (
          <div className="divide-y divide-gray-100" data-testid="featured-article-list">
            {articles.map(a => {
              const currentOrder = a.featured_order;
              const isPinned = currentOrder !== null && currentOrder !== undefined;
              return (
                <div key={a.slug} className="flex items-center gap-3 py-3 flex-wrap" data-testid={`featured-row-${a.slug}`}>
                  {a.featured_image ? (
                    <img src={a.featured_image} alt="" className="w-12 h-12 object-cover rounded border border-gray-200 flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                      <FileText className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-2">
                      {a.title}
                      {isPinned && (
                        <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded text-white ${SLOT_META[currentOrder]?.cls || 'bg-gray-500'}`}>
                          {SLOT_META[currentOrder]?.label || `Slot ${currentOrder + 1}`}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      {a.category || 'Uncategorized'} &middot; <span className="text-gray-400">{a.created_at?.slice(0, 10)}</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5" data-testid={`featured-controls-${a.slug}`}>
                    {[0, 1, 2].map(order => {
                      const active = currentOrder === order;
                      return (
                        <button
                          key={order}
                          onClick={() => pin(a.slug, order, a.title)}
                          disabled={busy === `${a.slug}:${order}`}
                          title={active ? `Pinned to ${SLOT_META[order].label}` : `Pin to ${SLOT_META[order].label}`}
                          className={`px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded transition-colors ${
                            active
                              ? `${SLOT_META[order].cls} text-white`
                              : 'bg-white text-gray-600 border border-gray-300 hover:border-gray-400 hover:text-gray-900'
                          } disabled:opacity-50`}
                          data-testid={`featured-pin-${a.slug}-${order}`}
                        >
                          {SLOT_META[order].label.replace('Slot ', '#')}
                        </button>
                      );
                    })}
                    {isPinned && (
                      <button
                        onClick={() => unpinOne(a.slug, a.title)}
                        disabled={busy === `unpin:${a.slug}`}
                        title="Unpin"
                        className="px-2 py-1.5 text-[10px] font-medium text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                        data-testid={`featured-unpin-row-${a.slug}`}
                      >
                        <PinOff className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
